import type { FunctionComponent } from 'react'
import { useEffect, useRef, useState } from 'react'
import type { BusyButtonProps } from '@ztechapps/webui.button'
import { BusyButtonLink } from '@ztechapps/webui.button'
import { HideIf, HorizontalStack, NullComponent } from '@ztechapps/webui.util'
import { InputGroup, Stack } from 'react-bootstrap'
import { TextAreaInput } from '../TextArea/TextAreaInput'

export type ChatInputProps = {
  message: string
  setMessage: (message: string) => void
  placeholder?: string
  onSend: (text: string) => void
  onAttach?: () => void
  maxLines?: number
  disabled?: boolean
  isBusy?: boolean
  shouldClearAfterSend?: boolean
  components?: {
    AttachButton?: FunctionComponent<BusyButtonProps>
    SendButton?: FunctionComponent<BusyButtonProps>
  }
}

export const ChatInput = ({
  message,
  setMessage,
  placeholder = 'Ask anything...',
  isBusy,
  disabled,
  onSend,
  onAttach,
  maxLines = 10,
  components,
  shouldClearAfterSend = false,
}: ChatInputProps) => {
  const singleLineRef = useRef(null)
  const multiLineRef = useRef(null)
  const [isMultiLine, setIsMultiLine] = useState(false)
  const [cursorPosition, setCursorPosition] = useState(0)
  const maxHeight = 24 * maxLines

  const TheAttachButton = components?.AttachButton || NullComponent
  const TheSendButton = components?.SendButton || SendButton

  useEffect(() => {
    // If message is empty, reset to single line
    if (message === '') {
      setIsMultiLine(false)
      return
    }

    // Determine which textarea to measure
    const activeTextarea = isMultiLine ? multiLineRef.current : singleLineRef.current

    if (activeTextarea) {
      activeTextarea.style.height = 'auto'

      if (isMultiLine) {
        //set only the height to the multi line.. the single line will retain as is
        const newHeight = Math.min(activeTextarea.scrollHeight, maxHeight)
        activeTextarea.style.height = `${newHeight}px`
      }

      // Calculate number of rows
      const lineHeight = 30
      const calculatedRows = Math.round(activeTextarea.scrollHeight / lineHeight)
      const shouldBeMultiLine = calculatedRows > 1

      // Only switch to multi-line, never back to single-line (unless empty)
      if (shouldBeMultiLine && !isMultiLine) {
        setIsMultiLine(true)
        // Focus will be handled in the next effect
      } else {
        // Restore cursor position on the active textarea
        activeTextarea.setSelectionRange(cursorPosition, cursorPosition)
      }
    }
  }, [message, isMultiLine, cursorPosition, maxHeight])

  useEffect(() => {
    // When layout changes, focus the appropriate textarea
    const targetTextarea = isMultiLine ? multiLineRef.current : singleLineRef.current
    if (targetTextarea) {
      targetTextarea.focus()
      targetTextarea.setSelectionRange(cursorPosition, cursorPosition)
    }
  }, [isMultiLine, cursorPosition])

  const handleChange = (e) => {
    setCursorPosition(e.target.selectionStart)
    setMessage(e.target.value)
  }

  const handleSubmit = () => {
    if (message.trim()) {
      onSend?.(message)
      if (shouldClearAfterSend) {
        setMessage('')
      }
      setCursorPosition(0)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <Stack>
      <HideIf condition={!isMultiLine}>
        <div className="border">
          <div>
            <TextAreaInput
              disabled={disabled || isBusy}
              name="multiline-textarea"
              inputRef={multiLineRef}
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={2}
              style={{
                overflowY: 'auto',
                resize: 'none',
                outline: 'none',
                border: 'none',
              }}
            />
          </div>
          <HorizontalStack className="justify-content-between p-3">
            <div>
              <TheAttachButton text="Attach" disabled={disabled} isBusy={isBusy} />
            </div>
            <HorizontalStack gap={2}>
              <TheSendButton onClick={handleSubmit} disabled={!message.trim() || disabled} isBusy={isBusy} />
            </HorizontalStack>
          </HorizontalStack>
        </div>
      </HideIf>
      <HideIf condition={isMultiLine}>
        <InputGroup className="d-flex align-items-center gap-2 px-2  border ">
          <TheAttachButton onClick={onAttach} />
          <div className="flex-fill">
            <TextAreaInput
              name="singleline-textarea"
              disabled={disabled || isBusy}
              inputRef={singleLineRef}
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={1}
              style={{
                overflowY: 'auto',
                resize: 'none',
                outline: 'none',
                border: 'none',
              }}
            />
          </div>

          <TheSendButton onClick={handleSubmit} disabled={!message.trim() || disabled} isBusy={isBusy} />
        </InputGroup>
      </HideIf>
    </Stack>
  )
}

const SendButton = (props: BusyButtonProps) => {
  return <BusyButtonLink text="Ask AI" icon="paper-plane" busyText="Asking..." {...props} />
}
